import {
  parseMultilineContent,
  stringifyMultilineContent,
  stringifyVariableAsIdentity,
} from "@repo/app-types/content/plaintext";
import type { MultilineContent } from "@repo/app-types/content/types";
import { standardRecordingSummaryTemplateBody } from "@repo/app-types/entities/messageTemplate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";

const DESCRIPTION =
  "Call summaries will be generated based on this template and will appear alongside transcripts.";

export interface SummaryTemplateSettingsFormProps {
  body: MultilineContent | null;
  onUpdateBody: (body: MultilineContent | null) => void;
  isReadOnly: boolean;
  /**
   * Live/published body. When provided (i.e. not `undefined`) and it differs
   * from the current body, a "See changes" toggle diffs the draft against it.
   * Leave undefined to render the plain editor without a diff affordance.
   */
  liveBody?: MultilineContent | null;
  /** Show a draft dot next to the label when there are unpublished changes. */
  hasUnpublishedChanges?: boolean;
  /**
   * Fill a bounded parent (the assessment editor's settings pane), with the
   * body scrolling inside it. Leave unset on a flowing page.
   */
  fillsHeight?: boolean;
}

function stringifyBody(body: MultilineContent | null): string {
  return body
    ? stringifyMultilineContent(body, {
        stringifyVariable: stringifyVariableAsIdentity,
      })
    : "";
}

type SummaryTab = "edit" | "diff";

export const SummaryTemplateSettingsForm: FC<
  SummaryTemplateSettingsFormProps
> = ({
  body,
  onUpdateBody,
  isReadOnly,
  liveBody,
  hasUnpublishedChanges,
  fillsHeight = false,
}) => {
  const [tab, setTab] = useState<SummaryTab>("edit");

  const bodyText = useMemo(() => stringifyBody(body), [body]);

  const handleBodyChange = useCallback(
    (newValue: string) => {
      if (newValue.trim() === "") {
        onUpdateBody(null);
        return;
      }

      onUpdateBody(parseMultilineContent(newValue, { parsesVariables: false }));
    },
    [onUpdateBody],
  );

  const hasLiveBaseline = liveBody !== undefined;
  const liveBodyText = hasLiveBaseline ? stringifyBody(liveBody) : "";
  const showDiffToggle = hasLiveBaseline && liveBodyText !== bodyText;
  const isDiffMode = showDiffToggle && tab === "diff";

  // Once the draft matches live again (after a publish/discard), leave diff mode
  // so the next edit lands in the editor instead of the read-only diff view.
  useEffect(() => {
    if (!showDiffToggle && tab !== "edit") {
      setTab("edit");
    }
  }, [showDiffToggle, tab]);

  // No live baseline provided: render the plain editor with its built-in label
  // (used by the playbook tree editor, which has no diff affordance).
  if (!hasLiveBaseline) {
    return (
      <div
        className={twMerge(
          "space-y-4 p-2",
          fillsHeight && "flex min-h-0 flex-1 flex-col",
        )}
      >
        <Textarea
          label="Summary template"
          placeholder={standardRecordingSummaryTemplateBody}
          value={bodyText}
          onChange={handleBodyChange}
          height="400px"
          fillHeight={fillsHeight}
          isDisabled={isReadOnly}
          description={DESCRIPTION}
        />
      </div>
    );
  }

  return (
    <div
      className={twMerge(
        "space-y-2",
        fillsHeight && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div className="flex min-h-8 items-center justify-between">
        <Label size={Size.SMALL}>
          <span className="flex items-center gap-1.5">
            Summary template
            {hasUnpublishedChanges && (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            )}
          </span>
        </Label>
        {showDiffToggle ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            className={twMerge("cursor-pointer pr-0", linkStyle())}
            onClick={() => setTab(tab === "edit" ? "diff" : "edit")}
          >
            {tab === "edit" ? "See changes" : "Edit"}
          </Button>
        ) : null}
      </div>
      {isDiffMode ? (
        <FieldDiffView
          className={
            fillsHeight
              ? "min-h-0 flex-1 overflow-y-auto"
              : "min-h-[400px] overflow-y-auto"
          }
          savedValue={liveBodyText}
          editedValue={bodyText}
        />
      ) : (
        <Textarea
          placeholder={standardRecordingSummaryTemplateBody}
          value={bodyText}
          onChange={handleBodyChange}
          height="400px"
          fillHeight={fillsHeight}
          isDisabled={isReadOnly}
          description={DESCRIPTION}
        />
      )}
    </div>
  );
};
